import type { ChannelSlice } from '../ChannelSlice';
import type { ColumnOptionsSlice } from '../ColumnOptionsSlice';
import type { ResultsAreaSlice } from '../ResultsAreaSlice';
import type { ScreenerWidgetAdapter } from '../ScreenerWidgetAdapter';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { Instant } from '@js-joda/core';
import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { Signal, ReadonlySignal } from '@preact/signals-react';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type {
    ConcreteIdea,
    Idea,
    NewIdea,
    ScreenerPlan,
} from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import debounce from 'lodash.debounce';
import type { SelectedIdea } from 'src/components/menus/IdeasMenuGrouped/types';
import type { TableStatus } from 'src/components/table-view/model';
import { IdeaDTO } from 'src/dtos/Idea';
import type { IdeaPlanDTO } from 'src/dtos/Idea';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { SCRANNER_CHANGE_EVENT } from 'src/features/scranner/dtos/ScrannerPlanDTO';
import { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import type { IdeaPlanTypes } from 'src/features/widget/components/WidgetHeader/WidgetHeaderMenus/IdeasMenus';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import { exportTableToCSV } from 'src/lib/util/exportTableCSV';
import type { ScreenerWidgetModel } from 'src/models/ScreenerWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import { makeHeaderElements } from 'src/models/WidgetModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
// TODO make a mutation in src/queries/ideas to avoid this
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { createIdea, updateIdea } from 'src/queries/http/ideas';
import { getIdeaByIdQuery, invalidateIdeaRecordsQueries } from 'src/queries/ideas';
import { getFilteredRowsCountQuery, getTotalRowsCountQuery } from 'src/queries/table';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import type { ScreenerWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

@injectable()
export class ScreenerWidgetModelImpl extends ReactiveInjectable implements ScreenerWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #stickyFilterSlice: StickyFilterSlice;
    #columnOptionsSlice: ColumnOptionsSlice;

    #name: string;
    #description: string;
    #sectionVisibility = signal<{ builder: boolean; results: boolean }>({ builder: true, results: true });
    #isDirty = signal(false);
    #widgetName: Signal<string> = signal('');
    #planListenerCleanup: (() => void) | null = null;

    // Column template state signals
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);

    // Validation state signal
    #isValidationModeEnabled: Signal<boolean> = signal(false);
    #userViewKey: Signal<string | undefined> = signal(undefined);

    // Selected fast ideas signal for widget header name display
    #selectedFastIdeas: Signal<SelectedIdea<IdeaPlanTypes>[]> = signal<SelectedIdea<IdeaPlanTypes>[]>([]);

    #connectionStatus: Signal<{
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }> = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    #tableRowCount: Signal<number> = signal(0);
    #rowCountTotalSignal: Signal<number> = signal(0);
    #refetchHandler: (() => void) | null = null;

    headerElements!: WidgetHeaderElement[];

    resultsAreaHeaderElements: WidgetHeaderElement[] = makeHeaderElements<ScreenerWidgetModel['eventBus']>([
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'stickyFilter', position: 1, priority: 70 },
        { type: 'spacer', position: 3, alwaysVisible: true },
        { type: 'columnTemplateMenu', position: 5, priority: 10, columnTemplateKey: 'ticker' },
        { type: 'settingsMenuNoScrolling', position: 6, priority: 60 },
        { type: 'tableRowCount', position: 8, priority: 30 },
        { type: 'connectionStatus', position: 9, priority: 40 },
        { type: 'exportCSVButton', position: 10, priority: 20 },
    ]);

    get canSaveIdea() {
        if (!this.#isDirty.value) {
            return false;
        }

        if ('id' in this.idea && 'permissions' in this.idea) {
            return doesAccessGrantAllowRequest(
                (this.idea as ConcreteIdea).permissions.effectiveDirectAccess || undefined,
                'writer',
            );
        }

        return false;
    }

    get canSubmit() {
        return this.#hasChangesSinceLastSubmit;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | string | null> {
        return this.#selectedColumnTemplate;
    }

    createNewScreener(): void {
        this.#lastSubmit.value = Instant.now();
        this.#plan = new ScreenerPlanDTO(undefined, this.queryClient, this.formulaService, this.slugMapService);
        this.#name = t('widgets::screener::name');
        this.#widgetName.value = t('widgets::screener::name');
        this.#description = '';

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                {
                    name: this.#name,
                    key: 'ticker',
                    description: this.#description,
                    //plan: this.#plan.toContract(),
                    defaultUniverseId: null,
                    defaultColumnTemplateId: null,
                    isTemplate: false,
                },
                this.#plan,
            ) as IdeaDTO<ScreenerPlanDTO>,
        );

        this.#initNewScreener();
    }

    // TODO: We could consider making this key a computed signal on user ID, tab ID, and a constant 'filter-row-count'
    //       and computing the query options based on that
    #filterRowCountTableUserViewKey = v4();

    constructor(
        @inject('ImportsManagerModel') @reacts public importsManager: ReactBindings['ImportsManagerModel'],
        @inject('ScreenerWidgetAdapter') private adapter: ScreenerWidgetAdapter,
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') private formulaService: ReactBindings['FormulaService'],
        @inject('TableModel') private totalTableModel: ReactBindings['TableModel'],
        @inject('TableModel') private filteredTableModel: ReactBindings['TableModel'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('ColumnOptionsSlice') columnOptionsSlice: ColumnOptionsSlice,
        @inject('ResultsAreaSlice') private resultsAreaSlice: ResultsAreaSlice,
        @inject('SlugMapService') private slugMapService: ReactBindings['SlugMapService'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#plan = new ScreenerPlanDTO(undefined, queryClient, formulaService, slugMapService);
        this.#name = t('widgets::screener::name');
        this.#widgetName.value = t('widgets::screener::name');
        this.#description = '';

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#columnOptionsSlice = columnOptionsSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.#initCounts();
        this.eventBus = this.#buildEventBus();
    }

    #hasChangesSinceLastSubmit = false;

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_MENU_HEADER_ELEMENTS
            : STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = makeHeaderElements<ScreenerWidgetModel['eventBus']>([
            ...baseElements,
            {
                type: 'ideasMenuItems',
                position: 2,
                overflowOnly: true,
                items: ['new', 'open', 'save', 'copy', 'delete'],
                ideaType: 'fast',
                ideaPlanTypes: ['screener'],
                selectType: 'single',
                showSelected: false,
                allowDelete: true,
            },
            { type: 'helpDocument', documentId: 'screener-overview', position: 39, priority: 5 },
            {
                type: 'sectionViewMenu',
                position: 80,
                priority: 6,
                viewConfig: [
                    { id: 'builder', label: 'Builder' },
                    { id: 'results', label: 'Results' },
                ],
            },
        ]);

        // Initialize slices with shared adapter (works for both workspace and standalone)
        this.#stickyFilterSlice.init(this.adapter);
        this.#columnOptionsSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);
        this.disposableStack.use(this.#columnOptionsSlice);

        if (tabId) {
            this.widgetData.init(tabId);

            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);

            this.#sectionVisibility.value = {
                builder: this.widget.displayingForm,
                results: this.widget.displayingResults,
            };

            // Listen for section visibility updates
            const unsubscribe = this.store
                .subscribeSelector((state) => {
                    const tabExists = state.tab.byId[tabId] !== undefined;
                    if (!tabExists) {
                        return null;
                    }
                    const widget = state.tab.byId[tabId]?.widget as ScreenerWidgetViewModel | undefined;
                    return widget
                        ? {
                              displayingForm: widget.displayingForm,
                              displayingResults: widget.displayingResults,
                          }
                        : null;
                })
                .handle((widgetData) => {
                    if (widgetData !== null) {
                        this.#sectionVisibility.value = {
                            builder: widgetData.displayingForm,
                            results: widgetData.displayingResults,
                        };
                    }
                });

            this.disposableStack.defer(() => {
                unsubscribe();
            });
        } else {
            // Standalone mode: initialize signal from adapter state (e.g. URL params)
            this.#sectionVisibility.value = {
                builder: this.adapter.getState().isShowingBuilder,
                results: this.adapter.getState().isShowingResults,
            };
        }

        const uniqueMountId = v4();
        const userViewKeyUnsubscribe = this.store
            .subscribeSelector((state) => useWidgetTabIdForUserViewKey.select(state), { leading: true })
            .handle((useTabId) => {
                this.#userViewKey.value = useTabId && tabId !== undefined ? tabId : uniqueMountId;
            });
        this.disposableStack.defer(userViewKeyUnsubscribe);

        // Initialize ResultsAreaSlice
        this.resultsAreaSlice.init(this.adapter, {
            columnTemplateKey: 'ticker',
            tabId,
            ideaMode: 'screener',
            ideaUserViewKeyScope: 'slow',
            userViewKeySignal: this.#userViewKey,
            validationModeSignal: this.#isValidationModeEnabled,
            onUserSelectedSymbol: (symbol) => this.adapter.userSelectedSymbol(symbol),
            stickyFilterEventBus: this.#stickyFilterSlice.eventBus,
            columnOptionsEventBus: this.#columnOptionsSlice.eventBus,
            devtoolsName: 'Screener/results',
        });
        this.disposableStack.use(this.resultsAreaSlice);

        // Sync resolved column template from slice to local signal
        this.disposableStack.defer(
            effect(() => {
                const loadedTemplate = this.resultsAreaSlice.columnTemplate.value;
                if (loadedTemplate && this.#selectedColumnTemplate.value !== loadedTemplate) {
                    this.#selectedColumnTemplate.value = loadedTemplate;
                }
            }),
        );

        // Wire table connection status and row count from slice
        this.disposableStack.defer(
            effect(() => {
                const table = this.resultsAreaSlice.table;
                this.#connectionStatus.value = {
                    status: table.connectionStatus.value,
                    progress: table.progress.value,
                    failureCount: table.failureCount.value,
                };
            }),
        );
        this.disposableStack.use(
            this.resultsAreaSlice.table.count$.subscribe((count) => {
                this.#tableRowCount.value = count;
            }),
        );

        this.#refetchHandler = () => this.resultsAreaSlice.table.refetch();

        // Update resultsAreaSlice with the widget plan idea whenever lastSubmit changes
        this.disposableStack.defer(
            effect(() => {
                void this.#lastSubmit.value;
                untracked(() => this.resultsAreaSlice.setWidgetPlanIdea(this.#plan.toTempIdea()));
            }),
        );

        // Initialize column template signal from adapter state
        this.#selectedColumnTemplate.value = this.adapter.getState().columnTemplate;

        // if we have an idea, set up the plan from it
        if (this.idea) {
            this.#name = this.idea.name ?? '';
            this.#widgetName.value =
                this.#name ||
                (tabId ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value : t('widgets::screener::name'));
            this.#description = this.idea.description ?? '';

            if (this.idea.plan.type === 'screener') {
                this.#plan = await ScreenerPlanDTO.fromPlan(
                    this.idea.plan,
                    this.queryClient,
                    this.formulaService,
                    this.slugMapService,
                    {
                        isDirty: this.adapter.getState().isIdeaDirty ?? false,
                    },
                );
                this.#onScreenerChange();
            }
        }

        this.#initNewScreener();
    }

    #initCounts() {
        // Get count for all possible tickers
        {
            this.totalTableModel.init({
                queryOptions: getTotalRowsCountQuery(),
                devtoolsName: 'Screener/totalCount',
            });

            this.disposableStack.use(
                this.totalTableModel.count$.subscribe((count) => {
                    this.rowCountTotal = count;
                    this.#rowCountTotalSignal.value = count;
                }),
            );
        }

        // init the count for the filtered table
        // updates handled when #onScreenerChange is called
        {
            this.filteredTableModel.init({
                queryOptions: {
                    queryKey: ['screener-rows-count-tcr', this.#plan.toContract()],
                    enabled: false,
                },
                initBounds: { firstRow: 0, windowSize: 0 },
                devtoolsName: 'Screener/filteredCount',
            });

            this.disposableStack.use(
                this.filteredTableModel.count$.subscribe((count) => {
                    this.rowCountFiltered = count;
                }),
            );

            this.disposableStack.defer(
                effect(() => {
                    const isLoading = this.filteredTableModel.connectionStatus.value !== 'connected';

                    if (this.rowCountFilteredLoading !== isLoading) {
                        this.rowCountFilteredLoading = isLoading;
                    }
                }),
            );
        }
    }

    #initNewScreener() {
        // trigger a submit, so the results view loads any time a new screener is used
        this.#lastSubmit.value = Instant.now();

        // Clean up previous plan's listener before adding new one
        // (this method can be called multiple times via useIdea/createNewScreener)
        this.#planListenerCleanup?.();

        // Capture plan reference for cleanup - this.#plan may change when useIdea is called
        const plan = this.#plan;

        // set up event bindings
        const listener = this.#onScreenerChange.bind(this);
        plan.changeTarget.addEventListener(SCRANNER_CHANGE_EVENT, listener);

        // Store cleanup function for this plan's listener
        this.#planListenerCleanup = () => {
            plan.changeTarget.removeEventListener(SCRANNER_CHANGE_EVENT, listener);
        };

        // Register with disposableStack for final cleanup when widget is destroyed
        this.disposableStack.defer(() => {
            this.#planListenerCleanup?.();
            this.#planListenerCleanup = null;
        });

        plan.changeTarget.dispatchEvent(new Event(SCRANNER_CHANGE_EVENT));
    }

    eventBus!: ScreenerWidgetModel['eventBus'];

    #buildEventBus(): ScreenerWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                .addEvent('widget::toggleSectionVisibility', (payload) => {
                    if (payload.sectionId === 'builder') {
                        this.isShowingBuilder = payload.show;
                    } else if (payload.sectionId === 'results') {
                        this.isShowingResults = payload.show;
                    }
                    this.#sectionVisibility.value = {
                        builder: this.isShowingBuilder,
                        results: this.isShowingResults,
                    };
                })
                // IPC-safe event handlers for idea editor actions
                .addEvent('widget::ideaEditorSave', async () => {
                    await this.saveIdea();
                })
                .addEvent('widget::ideaEditorSaveAs', async (payload) => {
                    await this.saveIdeaAs(payload.name);
                })
                .addEvent('widget::ideaEditorCreateNew', () => {
                    this.createNewScreener();
                })
                // Column template events
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: { columnTemplate: payload.templateId },
                        }),
                    );
                    this.#selectedColumnTemplate.value = payload.templateId;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = payload.columnTemplate;
                })
                // Column template library event
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Validation mode event
                .addEvent('widget::toggleValidationMode', () => {
                    this.#isValidationModeEnabled.value = !this.#isValidationModeEnabled.value;
                })
                // Export CSV event
                .addEvent('widget::exportCSV', () => {
                    void this.#exportCSV();
                })
                // Ideas selection events (enables "Open" menu item in ideasMenuItems)
                .addEvent('widget::onIdeasSelect', async (payload) => {
                    const selected = payload.ideas[0];
                    if (selected && !('universeId' in selected)) {
                        const ideaDto = selected as IdeaDTO;
                        const ideaId = ideaDto.idea.id;
                        if (ideaId) {
                            const apiIdea = await this.queryClient.fetchQuery(getIdeaByIdQuery(ideaId));
                            if (!apiIdea) {
                                throw new Error(`Idea ${ideaId} not found`);
                            }
                            const plan = await ScreenerPlanDTO.fromPlan(
                                apiIdea.plan as ScreenerPlan,
                                this.queryClient,
                                this.formulaService,
                                this.slugMapService,
                            );
                            this.useIdea(IdeaDTO.fromIdeaAndPlan(apiIdea, plan) as IdeaDTO<ScreenerPlanDTO>);
                        }
                    }
                })
                .addEvent('widget::selectSlowIdea', () => {
                    /* Screener does not use slow ideas */
                })
                // Signals
                // Idea editor capability signals (state-only, IPC-safe - NO function signals)
                .addSignal('widget::ideaEditorIsDirty', () => this.#isDirty)
                .addSignal('widget::ideaEditorCanSave', () => computed(() => this.canSaveIdea))
                .addSignal('widget::ideaEditorIsTemplate', () => computed(() => this.idea?.isTemplate ?? false))
                .addSignal('widget::ideaEditorUnsavedChangesLabel', () => computed(() => 'Unsaved Changes'))
                .addSignal('widget::sectionVisibility', () => this.#sectionVisibility)
                // Column template signals
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate || typeof columnTemplate === 'string') {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                // Validation signal
                .addSignal('widget::isValidationModeEnabled', () => this.#isValidationModeEnabled)
                // Scrolling signal (always false for Screener)
                // Table row count signals
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableTotalRowCount', () => this.#rowCountTotalSignal)
                // Connection status signal
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                // Ideas signals required by IdeasSignals interface
                .addSignal('widget::slowIdea', () => computed(() => null as IdeaDTO<IdeaPlanDTO> | null))
                .addSignal('widget::selectedSlowIdeas', () => computed(() => [] as SelectedIdea<IdeaPlanTypes>[]))
                .addSignal(
                    'widget::selectedFastIdeas',
                    () => this.#selectedFastIdeas as ReadonlySignal<SelectedIdea<IdeaPlanTypes>[]>,
                )
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchHandler?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge(this.#columnOptionsSlice.eventBus)
            .merge(this.resultsAreaSlice.eventBus)
            .merge(
                // Override widget::name to show idea name instead of tab name
                EventBusBuilder.create()
                    .addSignal('widget::name', () => this.#widgetName)
                    .build(),
            );
    }

    get isShowingBuilder() {
        return this.adapter.getState().isShowingBuilder;
    }

    set isShowingBuilder(value: boolean) {
        this.adapter.persist({ isShowingBuilder: value });
    }

    get isShowingResults() {
        return this.adapter.getState().isShowingResults;
    }

    set isShowingResults(value: boolean) {
        this.adapter.persist({ isShowingResults: value });
    }

    #lastSubmit: Signal<Instant> = signal(Instant.now());

    #rowCountFiltered = 0;
    get rowCountFiltered() {
        return this.#rowCountFiltered;
    }

    @reacts set rowCountFiltered(value: number) {
        this.#rowCountFiltered = value;
    }

    #rowCountFilteredLoading = true;
    get rowCountFilteredLoading() {
        return this.#rowCountFilteredLoading;
    }

    @reacts set rowCountFilteredLoading(value: boolean) {
        this.#rowCountFilteredLoading = value;
    }

    #rowCountTotal = 0;
    get rowCountTotal() {
        return this.#rowCountTotal;
    }

    @reacts set rowCountTotal(value: number) {
        this.#rowCountTotal = value;
    }

    #updateFilterCount = debounce(() => {
        this.filteredTableModel.updateQueryOptions(
            getFilteredRowsCountQuery(this.#plan, this.queryClient, this.#filterRowCountTableUserViewKey),
        );
    }, 1000);

    #onScreenerChange() {
        // this will be called each time the screener changes, including when we first load it.
        // so set hasChangesSinceLastSubmit to true if the screener is dirty
        this.#hasChangesSinceLastSubmit = this.#plan.isDirty;
        this.#isDirty.value = this.#plan.isDirty;

        // Sync dirty state to Redux so workspace save can serialize by reference when clean
        this.adapter.persist({ isIdeaDirty: this.#plan.isDirty });

        this.#updateFilterCount();

        this.rerender();
    }

    async saveIdea(): Promise<ConcreteIdea> {
        const canPut = (_idea: Idea | NewIdea): _idea is ConcreteIdea => {
            return Boolean(this.idea.id);
        };

        const contractIdea = this.toIdea();

        const ideaToSave = canPut(contractIdea) ? contractIdea : null;

        if (!ideaToSave) {
            throw new Error('Cannot save idea without an id');
        }

        const newIdea = (await updateIdea(ideaToSave)) as ConcreteIdea & {
            plan: ScreenerPlan;
        };

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                await ScreenerPlanDTO.fromPlan(
                    newIdea.plan,
                    this.queryClient,
                    this.formulaService,
                    this.slugMapService,
                ),
            ) as IdeaDTO<ScreenerPlanDTO>,
        );

        invalidateIdeaRecordsQueries(this.queryClient);

        return newIdea;
    }

    async saveIdeaAs(name: string): Promise<ConcreteIdea> {
        const ideaToSave = this.toIdea(name);

        if (!ideaToSave.name) {
            throw new Error('Cannot save idea without a name');
        }

        const newIdea = (await createIdea(ideaToSave)) as ConcreteIdea & {
            plan: ScreenerPlan;
        };

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                await ScreenerPlanDTO.fromPlan(
                    newIdea.plan,
                    this.queryClient,
                    this.formulaService,
                    this.slugMapService,
                ),
            ) as IdeaDTO<ScreenerPlanDTO>,
        );

        invalidateIdeaRecordsQueries(this.queryClient);

        return newIdea;
    }

    toIdea(name?: string): NewIdea {
        return {
            ...this.idea,
            name: name ?? this.#name,
            description: this.#description,
            plan: this.#plan.toContract(),
        };
    }

    #plan: ScreenerPlanDTO;

    get screener() {
        return this.#plan;
    }

    get splitPercentage() {
        return this.adapter.getState().splitPercentage;
    }

    set splitPercentage(value: number) {
        this.adapter.persist({ splitPercentage: value });
    }

    get table() {
        return this.resultsAreaSlice.table;
    }

    submit() {
        this.#lastSubmit.value = Instant.now();
        this.#hasChangesSinceLastSubmit = false;
        this.rerender();
    }

    userSelectedSymbol(symbol: string) {
        this.adapter.userSelectedSymbol(symbol);
    }

    get idea() {
        return this.adapter.getState().idea;
    }

    async useIdea(idea: IdeaDTO<ScreenerPlanDTO>) {
        this.adapter.persist({
            idea: {
                ...idea.idea,
                plan: idea.plan.toContract(),
            } as Idea & { plan: ScreenerPlan },
        });

        this.#name = idea.name ?? '';
        this.#widgetName.value =
            this.#name ||
            (this.widgetData.tabId
                ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value
                : t('widgets::screener::name'));
        this.#description = idea.idea.description ?? '';

        // Update selected fast ideas signal for widget header display
        const ideaId = idea.idea.id;
        this.#selectedFastIdeas.value = ideaId ? [{ type: 'idea', id: ideaId }] : [];

        this.#plan = idea.plan;
        this.#initNewScreener();
    }

    private get widget() {
        return this.widgetData.widget as ScreenerWidgetViewModel;
    }

    async #exportCSV(): Promise<void> {
        const columnTemplate = this.resultsAreaSlice.columnTemplate.value;
        if (!columnTemplate || typeof columnTemplate === 'string') {
            return;
        }

        const validationColumns = this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;

        const columnNames = [
            ...columnTemplate.columns.map((col) => ({
                id: col.id,
                name: col.name,
            })),
            ...validationColumns.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await exportTableToCSV(this.resultsAreaSlice.table, columnNames, t);
    }
}
